import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { proxy } from 'valtio';
import { faListMusic } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { LikeReleaseButton } from '@components/Likes/Button';
import { Button } from '@components/main-components/button/Button';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { AddToPlaylistModal } from '@components/modals/AddToPlaylistModal/AddToPlaylistModal';
import { AudioPlayerCollectModal } from '@components/modals/Collect/AudioPlayerCollectModal';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { useMediaQuery } from '@context/mediaQuery';
import { ModalContext, ModalType } from '@context/modal';
import { ToastContext } from '@context/toast';
import type { ReleaseTrackAudioInfoFragment } from '@graphql/reactQuery';
import { trackClickEvent } from '@lib/analytics';
import { useAudioController } from '@store/audio/AudioController';
import { webappUriPath } from '@utils/url';
import { ControlButtons } from './ControlButtons';
import { QueueButton } from './QueueButton';
import { QueueSheet } from './QueueSheet';
import { SongInfo } from './SongInfo';
import { Timeline } from './Timeline';
import { VolumeButton } from './VolumeButton';

export const AUDIO_PLAYER_ID = 'AudioPlayer';

const MinimizedPlayer = dynamic(() =>
  import('@components/AudioPlayerMobile/MinimizedPlayer').then(mod => mod.MinimizedPlayer),
);

export const AudioPlayerExtra = proxy<{ lastReleasePlayed: ReleaseTrackAudioInfoFragment | null }>({
  lastReleasePlayed: null,
});

export const AudioPlayer = () => {
  const {
    activeTrackId,
    hideAudioPlayer,
    activePlaylistId,
    activePlaylistReleaseIds,
    trackAudioUrl,
    activeTrackReleaseInfo,
  } = useAudioController();
  const { tablet } = useMediaQuery();

  const [isQueueOpen, setQueueOpen] = useState(false);
  const { openModal } = ModalContext.useContainer();
  const { isAuthenticatedWithAPI } = useAuth();
  const { login } = useConnectedAuth();
  const { openToast } = ToastContext.useContainer();
  const { asPath } = useRouter();

  const releaseId = activeTrackReleaseInfo?.id;

  useEffect(() => {
    if (!activeTrackReleaseInfo) return;

    AudioPlayerExtra.lastReleasePlayed = activeTrackReleaseInfo;
  }, [activeTrackReleaseInfo]);

  const hideCollectPlaylist = useMemo(
    () =>
      activePlaylistId &&
      releaseId &&
      asPath.startsWith(`/playlist/${activePlaylistId}`) &&
      activePlaylistReleaseIds.includes(releaseId),
    [activePlaylistId, releaseId, activePlaylistReleaseIds, asPath],
  );

  if (!activeTrackId || hideAudioPlayer) return null;
  const releasePath = !!activeTrackReleaseInfo?.webappUri
    ? webappUriPath(activeTrackReleaseInfo?.webappUri)
    : null;
  const showCollect = releasePath ? !asPath.startsWith(releasePath) : false;

  const onAddToPlaylistClick = () => {
    if (!isAuthenticatedWithAPI) {
      trackClickEvent({
        event: 'Sign in Clicked',
        properties: {
          sourceComponent: 'Add to Playlist Modal',
        },
      });
      login();
      return;
    } else {
      if (!releaseId) {
        return openToast({
          text: 'This track is not available for playlisting yet.',
          variant: 'error',
        });
      }
      trackClickEvent({
        event: 'Add to Playlist Clicked',
        properties: {
          sourceComponent: 'Audio Player',
          associationId: releaseId,
          associationType: 'Release',
        },
      });
      return openModal(ModalType.ADD_TO_PLAYLIST, {
        body: <AddToPlaylistModal releaseId={releaseId} />,
      });
    }
  };

  return (
    <>
      {!tablet ? <MinimizedPlayer /> : null}
      <div
        className="fixed bottom-0 z-audioPlayerContainer hidden h-20 max-h-20 w-full flex-col items-center border-t border-t-neutral200 bg-white md:flex [&_.like-icon]:[&_svg]:!h-4 [&_.like-icon]:[&_svg]:!w-4"
        style={{
          WebkitTapHighlightColor: 'transparent',
        }}
        id={AUDIO_PLAYER_ID}
      >
        <div className="flex w-full justify-between p-3">
          <div className="flex h-full w-full overflow-hidden md:max-w-[360px]">
            <SongInfo />
          </div>
          <div className="flex h-full max-w-[1440px] flex-1 flex-col items-center">
            <div className="mb-2 flex h-[30px] w-[224px] items-center justify-between">
              <ControlButtons />
            </div>
            <div className="w-full flex-1">
              <Timeline />
            </div>
          </div>
          <div className="flex h-full w-full max-w-[360px] items-center justify-end gap-2 overflow-hidden pr-1">
            {releaseId && (
              <LikeReleaseButton
                releaseId={releaseId}
                disabled={!trackAudioUrl}
                source="Audio Player"
                iconSize={16}
              />
            )}
            <IconButton
              onClick={onAddToPlaylistClick}
              icon={<FontAwesomeIcon icon={faListMusic} />}
              size={32}
              iconSize={15}
            />
            <VolumeButton />
            <QueueButton isQueueOpen={isQueueOpen} setQueueOpen={setQueueOpen} />
            {showCollect && !!releaseId && (
              <Button
                variant="primary"
                size="S"
                label="Collect"
                className={clsx('min-w-none w-fit !px-3 !py-2', {
                  'lt:hidden': hideCollectPlaylist,
                })}
                onClick={() => {
                  trackClickEvent({
                    event: 'Collect Now Clicked',
                    properties: {
                      associationId: releaseId,
                      associationType: 'Release',
                      sourceComponent: 'Audio Player',
                    },
                  });
                  openModal(ModalType.AUDIO_PLAYER_COLLECT, {
                    body: <AudioPlayerCollectModal releaseId={releaseId} />,
                  });
                }}
              />
            )}
          </div>
        </div>
      </div>
      <QueueSheet isOpen={isQueueOpen} onClose={() => setQueueOpen(false)} />
    </>
  );
};
